import { Filters, ForbiddenTypes, PaginationProps, PaginationReturn, SmartQueryResult } from '@common/types/service';
import { handleError } from '@utils/common';
import { matchPath } from 'react-router-dom';

const getPaginationString = (paginationProps: PaginationProps) => {
    let paginationStr = '';
    for (const prop of Object.keys(paginationProps)) {
        const value = paginationProps[prop as keyof PaginationProps];
        if (Array.isArray(value)) {
            value.forEach((item) => {
                paginationStr += `${prop}=${item}&`;
            });
        } else {
            paginationStr += `${prop}=${value}&`;
        }
    }
    return paginationStr;
};

const getFilterString = (filters: Filters, field?: string) => {
    let filterStr = '';
    if (!field) {
        for (const filter of Object.keys(filters)) {
            filters[filter]?.forEach((value) =>
                !!value ? (filterStr += `${filter}=${value === 'blank' ? '' : value}&`) : null
            );
        }
    } else {
        filterStr = `${field}=`;
        for (const filter of Object.keys(filters)) {
            filterStr +=
                filters[filter]
                    ?.filter((value) => !!value)
                    .map((value) => (value === 'blank' ? '' : value))
                    .join(',') ?? '';
        }
    }
    return filterStr;
};

const serializeQueryParams = <T extends object>(params: T) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach((item) => searchParams.append(key, String(item)));
        } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
        }
    });
    return searchParams.toString();
};

const handleQueryStarted = async <T>(queryFulfilled: Promise<T>, title: string) => {
    try {
        await queryFulfilled;
    } catch (err) {
        handleError(title, err);
    }
};

interface ApiErrorResponse {
    status: number;
    data: { detail?: string; [key: string]: string | undefined };
}

interface RTKApiErrorResponse {
    error: {
        message: string;
    };
    payload: {
        status?: number;
        originalStatus?: number;
    };
}

const isRTKApiErrorResponse = (error: unknown): error is RTKApiErrorResponse => {
    return (
        typeof error === 'object' &&
        error != null &&
        'error' in error &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        typeof (error as any).error.message === 'string' &&
        'payload' in error &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (typeof (error as any).payload?.status === 'number' ||
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            typeof (error as any).payload?.originalStatus === 'number')
    );
};

const isApiResponseError = (error: unknown): error is ApiErrorResponse => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return typeof error === 'object' && error != null && 'status' in error && typeof (error as any).status === 'number';
};

const isPaginationReturn = <T extends { id: number | string }>(response: unknown): response is PaginationReturn<T> =>
    typeof response === 'object' && response !== null && 'count' in response && 'results' in response;

const getForbiddenInformation = (caseValue?: ForbiddenTypes) => {
    switch (caseValue) {
        case ForbiddenTypes.DASHBOARD_EVENT_DETAIL:
            return {
                redirect: '/dashboard/events',
                text: 'Return to Event List',
            };
        case ForbiddenTypes.DASHBOARD_EVENT_LIST:
            return {
                redirect: '/events',
                text: 'Return to Event List',
            };
        case ForbiddenTypes.PUBLIC_EVENT_DETAIL:
            return {
                redirect: '/events',
                text: 'Return to Event List',
            };
        case ForbiddenTypes.PUBLIC_EVENT_LIST:
        default:
            return {
                redirect: '/',
                text: 'Return to Homepage',
            };
    }
};

const ForbiddenTypesRouteDict: Record<string, ForbiddenTypes> = {
    '/app/events/:type/:eventId': ForbiddenTypes.PUBLIC_EVENT_DETAIL,
    '/app/events': ForbiddenTypes.PUBLIC_EVENT_LIST,
    '/app/dashboard/events': ForbiddenTypes.DASHBOARD_EVENT_LIST,
    '/app/dashboard/events/:type/:eventId': ForbiddenTypes.DASHBOARD_EVENT_DETAIL,
};

const getMatchCase = () => {
    const pathname = new URL(window.location.pathname, 'http://stub').pathname;
    for (const route of Object.keys(ForbiddenTypesRouteDict)) {
        if (matchPath(route, pathname)) return ForbiddenTypesRouteDict[route];
    }
};

// Generic smart hook factory
const createSmartQueryHook = <
    PropsBase,
    PropsPaginated extends PropsBase,
    TArray,
    TPagination,
    Hook extends (props: PropsBase | PropsPaginated, options?: any) => any
>(
    hook: Hook,
    isPaginated: (props: PropsBase | PropsPaginated) => props is PropsPaginated
) => {
    type SmartResult<P> = P extends PropsPaginated
        ? SmartQueryResult<TPagination, Hook>
        : SmartQueryResult<TArray, Hook>;

    function useSmartQuery<P extends PropsBase | PropsPaginated>(
        props: P,
        options?: Parameters<Hook>[1]
    ): SmartResult<P> {
        const result = hook(props, options);
        if (isPaginated(props)) {
            return result as SmartQueryResult<TPagination, Hook>;
        }
        return result as SmartQueryResult<TArray, Hook>;
    }
    return useSmartQuery;
};

export {
    createSmartQueryHook,
    getFilterString,
    getForbiddenInformation,
    getMatchCase,
    getPaginationString,
    handleQueryStarted,
    isApiResponseError,
    isPaginationReturn,
    isRTKApiErrorResponse,
    serializeQueryParams,
};
export type { ApiErrorResponse };
